import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { DragonfruitPortal } from '../../portal';

export interface InternalCursorTooltipPlacementProps {
  tooltip: JSX.Element;
  /**
   * Whether the content wrapper should fill its parent. This can be used in the case where e.g.
   * you are wrapping a button that you want to fill its parent.
   */
  shouldContentWrapperFillContainerWidth?: boolean;
  shouldContentWrapperFillContainerHeight?: boolean;
  /**
   * When false, the tooltip will not be shown, regardless of mouse positioning.
   * When undefined or true, the tooltip will behave as normal, displaying on the mouse
   * cursor when hovering.
   */
  overrideShow?: boolean;
  /**
   * Offset from the cursor position in pixels. Positive x moves right, positive y moves down.
   * Defaults to { x: 2, y: -2 } (2px right and 2px up from cursor).
   */
  offset?: { x: number; y: number };
  maxWidth: number;
  children?: React.ReactNode;
}

const WINDOW_PADDING = 24;
export const TOOLTIP_MAX_WIDTH = 300;
const OPEN_LEFT_THRESHOLD = 350;
const X_TRANSLATE = 'translateX(-100%)';
const Y_TRANSLATE = 'translateY(-100%)';

const InternalCursorTooltipPlacement: React.FC<InternalCursorTooltipPlacementProps> = (props) => {
  const [x, setX] = useState<number | undefined>(undefined);
  const [y, setY] = useState<number | undefined>(undefined);

  const ref = useRef<HTMLDivElement>(null);

  const onMouseMove = (ev: React.MouseEvent): void => {
    if (overrideShow === false) {
      // Do not show the Tooltip is overrideShow == false.
      return;
    }
    setX(ev.pageX);
    setY(ev.pageY);
  };

  const onMouseLeave = (): void => {
    setX(undefined);
    setY(undefined);
  };

  const {
    children,
    tooltip,
    shouldContentWrapperFillContainerWidth,
    shouldContentWrapperFillContainerHeight,
    overrideShow,
    offset = { x: 2, y: -2 },
    maxWidth,
  } = props;

  const top = y !== undefined ? y + offset.y : undefined;
  const left = x !== undefined ? x + offset.x : undefined;

  useLayoutEffect(() => {
    if (ref.current && top !== undefined && left !== undefined) {
      let transform = '';
      if (ref.current.offsetHeight <= top - WINDOW_PADDING) {
        transform = Y_TRANSLATE;
      }

      if (ref.current.offsetWidth + left > window.innerWidth - WINDOW_PADDING) {
        transform += X_TRANSLATE;
      }

      ref.current.style.transform = transform;
    }
  });

  return (
    <div
      className={classNames('dragonfruit-tooltip-wrapper', {
        'should-fill-container-width': shouldContentWrapperFillContainerWidth,
        'should-fill-container-height': shouldContentWrapperFillContainerHeight,
      })}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {children}
      {x !== undefined && top !== undefined && left !== undefined && (
        <DragonfruitPortal>
          <div
            style={{
              left: left - maxWidth,
              top,
              maxWidth: maxWidth * 2,
            }}
            className="dragonfruit-tooltip-placement cursor"
            role="tooltip"
          >
            <div
              className="tooltip-wrapper"
              style={{
                transform: x > window.innerWidth - OPEN_LEFT_THRESHOLD ? X_TRANSLATE : undefined,
                left: maxWidth,
                top: 0,
                width: 'max-content',
              }}
              ref={ref}
            >
              {tooltip}
            </div>
          </div>
        </DragonfruitPortal>
      )}
    </div>
  );
};

export default InternalCursorTooltipPlacement;
